import { IDoohFilterVenueType } from '@/Interfaces/Forecaster/Dooh.interface';
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import { DROPDOWN_HEIGHT } from '@/constants/Forecaster/venues';
import type { FilterVenueTypesNode } from '@/types/Forecaster/VenueNode.types';
import { Box, Checkbox, MenuItem } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

interface IProps {
  node: FilterVenueTypesNode;
  selected: IDoohFilterVenueType[];
  visibleIds: Set<string> | null;

  onChange: (node: FilterVenueTypesNode) => void;
}

const VenueTypeColumn = ({ node, selected, visibleIds, onChange }: IProps) => {
  const [hover, setHover] = useState<boolean>(false);
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const menuItemRef = useRef<HTMLLIElement | null>(null);

  const isVisible = useMemo(() => !visibleIds || visibleIds.has(node.id), [visibleIds, node]);

  if (!isVisible) return null;

  const hasVisibleChildren = useMemo(
    () => node.children.some(child => !visibleIds || visibleIds.has(child.id)),
    [node, visibleIds],
  );

  useEffect(() => {
    if (hover && menuItemRef.current) {
      const menuRect = menuItemRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - menuRect.bottom;
      const spaceAbove = menuRect.top;
      if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
        setOpenUpwards(true);
      } else {
        setOpenUpwards(false);
      }
    }
  }, [hover]);

  return (
    <Box sx={{ position: 'relative' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <MenuItem
        ref={menuItemRef}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={selected.some(v => v.id === node.id)}
            onChange={() => onChange(node)}
            onClick={e => e.stopPropagation()}
          />
          {node.name}
        </Box>

        {hasVisibleChildren && <ChevronRightIcon />}
      </MenuItem>
      {hover && hasVisibleChildren && (
        <Box
          sx={[
            {
              position: 'absolute',
              left: '100%',
              minWidth: 300,
              backgroundColor: '#fff',
              boxShadow: 4,
              zIndex: 10,
            },
            openUpwards
              ? {
                  top: 'auto',
                }
              : {
                  top: 0,
                },
            openUpwards
              ? {
                  bottom: 0,
                }
              : {
                  bottom: 'auto',
                },
          ]}
        >
          {node.children.map(child => {
            if (visibleIds && !visibleIds.has(child.id)) {
              return null;
            }

            return (
              <VenueTypeColumn
                key={child.id}
                node={child}
                selected={selected}
                onChange={onChange}
                visibleIds={visibleIds}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default VenueTypeColumn;
